import { useState } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Dialog,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Emoji,
    FieldGroup,
    Group,
    Heading,
    Label,
    LoadingIndicator,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';

import { apiClient } from '../../state';
import { TranslationLanguagePicker } from '../Translations';
import { SpaceLink } from './SpaceLink';

type EditSpaceLanguageDialogProps = {
    space: api.Space;
    defaultLanguage?: api.TranslationLanguage;
};

/**
 * Button to edit the language of a space.
 */
export function EditSpaceLanguageButton(props: EditSpaceLanguageDialogProps) {
    const { space, defaultLanguage = api.TranslationLanguage.En } = props;
    const currentLanguage = space.language ?? defaultLanguage;

    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    leadingIcon={
                        <Emoji code={translationLanguageDescriptors[currentLanguage].emoji} />
                    }
                    label={translationLanguageDescriptors[currentLanguage].label}
                    kind="secondary"
                    trailingIcon={Icon.Edit}
                />
            </DialogTrigger>
            <EditSpaceLanguageDialog {...props} />
        </DialogRoot>
    );
}

/**
 * Dialog to edit the language of a space.
 */
export function EditSpaceLanguageDialog(props: EditSpaceLanguageDialogProps) {
    return (
        <Dialog>
            <EditSpaceLanguageDialogContent {...props} />
        </Dialog>
    );
}

function EditSpaceLanguageDialogContent(props: EditSpaceLanguageDialogProps) {
    const { space, defaultLanguage = api.TranslationLanguage.En } = props;

    const [selectedLanguage, setSelectedLanguage] = useState<api.TranslationLanguage | undefined>(
        space.language ?? defaultLanguage
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const closeDialog = useDialogClose();

    const handleSubmit = async () => {
        if (selectedLanguage === space.language || !selectedLanguage) {
            closeDialog();
            return;
        }

        setIsSubmitting(true);

        try {
            await toast.catch(async () => {
                await apiClient.spaces.updateSpaceById(space.id, {
                    language: selectedLanguage,
                });

                analytics.logEvent(
                    analytics.createSpaceEvent(space, {
                        type: 'space_edit_language',
                        language: selectedLanguage,
                    })
                );

                toast.success('Space language updated');
            }, 'Error while updating space language');

            closeDialog();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <DialogHeader>
                <Heading>Edit Space Language</Heading>
                <Description>
                    Choose the language for the space <SpaceLink spaceId={space.id} />. This will
                    affect the user interface language in your published site.
                </Description>
            </DialogHeader>

            <DialogBody>
                <FieldGroup orientation="vertical">
                    <Group>
                        <Label>Language</Label>
                        <Description>
                            Select the language for your space content and user interface.
                        </Description>
                    </Group>

                    <TranslationLanguagePicker
                        value={selectedLanguage ?? null}
                        onChange={(value) => setSelectedLanguage(value)}
                    />
                </FieldGroup>
            </DialogBody>

            <DialogFooter>
                <DialogCloseButton />
                <DialogActionButton
                    label="Save"
                    onPress={handleSubmit}
                    disabled={isSubmitting || selectedLanguage === space.language}
                    leadingIcon={isSubmitting ? <LoadingIndicator /> : undefined}
                />
            </DialogFooter>
        </>
    );
}
